import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Calendar, Clock, ArrowLeft, Share2, User, Compass, Mountain, Check, Copy } from 'lucide-react';
import { PortableText, PortableTextComponents } from '@portabletext/react';
import { sanityClient, urlFor, SanityPost } from '../lib/sanity';
import { updateSEO } from '../lib/seo';
import { Language } from '../translations';
import { getLocalizedPath } from '../lib/i18n';

interface BlogPostProps {
    t: any;
    lang: Language;
}

const FALLBACK_BLOG_IMAGE = 'https://iuixlsqrlfkfnvnckgij.supabase.co/storage/v1/object/public/expeditions/Laguna%20Cerro%20Castillo/trekking-laguna-cerro-castillo-turquesa.jpg';

// Componentes personalizados para Portable Text con la estética de Fisura Expeditions
const portableTextComponents: PortableTextComponents = {
    types: {
        image: ({ value }) => {
            const imgUrl = value?.asset ? urlFor(value).width(1200).quality(85).url() : null;
            if (!imgUrl) return null;
            return (
                <figure className="my-10">
                    <img
                        src={imgUrl}
                        alt={value?.alt || 'Fisura Expeditions'}
                        className="rounded-xl w-full object-cover shadow-2xl border border-rock/70"
                    />
                    {value?.caption && (
                        <figcaption className="text-center text-xs text-gray-400 mt-3 italic font-light">
                            {value.caption}
                        </figcaption>
                    )}
                </figure>
            );
        },
    },
    block: {
        h2: ({ children }) => (
            <h2 className="text-2xl sm:text-3xl font-title uppercase tracking-wider text-white mt-12 mb-4 border-l-2 border-[#00d4ff] pl-4">
                {children}
            </h2>
        ),
        h3: ({ children }) => (
            <h3 className="text-xl sm:text-2xl font-title uppercase tracking-wider text-[#00d4ff] mt-8 mb-3">
                {children}
            </h3>
        ),
        normal: ({ children }) => (
            <p className="text-gray-300 text-base sm:text-lg leading-relaxed font-light mb-6 text-justify">
                {children}
            </p>
        ),
        blockquote: ({ children }) => (
            <blockquote className="border-l-4 border-[#00d4ff] bg-[#1a1512]/60 p-6 my-8 italic text-gray-200 rounded-r-xl font-serif text-lg leading-relaxed text-justify">
                {children}
            </blockquote>
        ),
    },
    marks: {
        strong: ({ children }) => (
            <strong className="font-bold text-white">{children}</strong>
        ),
        em: ({ children }) => <em className="italic text-gray-200">{children}</em>,
        link: ({ value, children }) => {
            const isInternal = value?.href?.startsWith('/') || value?.href?.includes('fisuraexpeditions.com');
            return (
                <a
                    href={value?.href}
                    target={isInternal ? '_self' : '_blank'}
                    rel={isInternal ? undefined : 'noopener noreferrer'}
                    className="text-[#00d4ff] font-medium underline underline-offset-4 decoration-[#00d4ff]/60 hover:text-white hover:decoration-white transition-colors duration-200 cursor-pointer [&_*]:!text-inherit"
                >
                    {children}
                </a>
            );
        },
    },
    list: {
        bullet: ({ children }) => (
            <ul className="list-disc list-inside my-6 space-y-2.5 text-gray-300 text-base sm:text-lg font-light text-justify">{children}</ul>
        ),
        number: ({ children }) => (
            <ol className="list-decimal list-inside my-6 space-y-2.5 text-gray-300 text-base sm:text-lg font-light text-justify">{children}</ol>
        ),
    },
};

const BlogPost: React.FC<BlogPostProps> = ({ t, lang }) => {
    const { slug } = useParams<{ slug: string }>();
    const navigate = useNavigate();
    const [post, setPost] = useState<SanityPost | null>(null);
    const [loading, setLoading] = useState(true);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (!slug) return;

        const fetchPost = async () => {
            setLoading(true);
            try {
                const query = `*[_type == "post" && slug.current == $slug][0] {
                    _id,
                    title,
                    slug,
                    language,
                    isAutoTranslated,
                    translationGroupId,
                    publishedAt,
                    excerpt,
                    mainImage,
                    body,
                    content,
                    categories[]->{ _id, title, slug },
                    author->{ _id, name, role, image, bio },
                    readTimeMinutes,
                    seoTitle,
                    seoDescription,
                    seoKeywords
                }`;

                const data = await sanityClient.fetch<SanityPost>(query, { slug });
                setPost(data || null);

                if (data) {
                    const currentUrl = typeof window !== 'undefined' ? window.location.href : `https://fisuraexpeditions.com/blog/${data.slug.current}`;
                    const imgUrl = data.mainImage?.asset ? urlFor(data.mainImage).width(1200).url() : FALLBACK_BLOG_IMAGE;
                    
                    // Actualización de SEO Dinámico + Schema Article
                    updateSEO({
                        title: `${data.seoTitle || data.title} | Fisura Expeditions`,
                        description: data.seoDescription || data.excerpt || 'Artículo del blog de Fisura Expeditions en Cerro Castillo.',
                        keywords: data.seoKeywords || ['Fisura Expeditions', 'Cerro Castillo', 'Aysén'],
                        image: imgUrl,
                        url: currentUrl,
                        type: 'article',
                        schemaData: {
                            '@context': 'https://schema.org',
                            '@type': 'BlogPosting',
                            'headline': data.title,
                            'description': data.excerpt,
                            'image': [imgUrl],
                            'datePublished': data.publishedAt,
                            'author': {
                                '@type': 'Person',
                                'name': data.author?.name || 'Fisura Expeditions'
                            },
                            'publisher': {
                                '@type': 'Organization',
                                'name': 'Fisura Expeditions',
                                'logo': {
                                    '@type': 'ImageObject',
                                    'url': 'https://fisuraexpeditions.com/logo-fisura-white.png'
                                }
                            },
                            'mainEntityOfPage': {
                                '@type': 'WebPage',
                                '@id': currentUrl
                            }
                        }
                    });
                }
            } catch (error) {
                console.error('Error cargando artículo:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchPost();
    }, [slug]);

    // Redirección inteligente: si el lector cambia de idioma con la bandera,
    // buscamos si existe la versión de este mismo artículo en el nuevo idioma
    useEffect(() => {
        const currentPostLang = post?.language || 'es';
        if (!post || currentPostLang === lang) return;

        const findTranslatedPost = async () => {
            try {
                const groupId = post.translationGroupId || post._id.replace('drafts.', '');
                const query = `*[_type == "post" && (language == $lang || (!defined(language) && $lang == 'es')) && (translationGroupId == $groupId || _id == $groupId || _id == "auto-trans-" + $groupId + "-" + $lang)][0] {
                    slug
                }`;
                const sibling = await sanityClient.fetch<{ slug: { current: string } }>(query, { lang, groupId });
                if (sibling?.slug?.current && sibling.slug.current !== slug) {
                    navigate(`/blog/${sibling.slug.current}`, { replace: true });
                }
            } catch (err) {
                console.warn('No se pudo encontrar traducción para este artículo:', err);
            }
        };

        findTranslatedPost();
    }, [lang, post, slug, navigate]);

    const formatDate = (dateString: string) => {
        try {
            const locale = lang === 'en' ? 'en-US' : lang === 'pt' ? 'pt-BR' : 'es-CL';
            return new Date(dateString).toLocaleDateString(locale, {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
        } catch {
            return dateString;
        }
    };

    const handleCopyLink = () => {
        if (typeof window !== 'undefined') {
            navigator.clipboard.writeText(window.location.href);
            setCopied(true);
            setTimeout(() => setCopied(false), 3000);
        }
    };

    if (loading) {
        return (
            <div className="bg-[#0a0a0a] min-h-screen text-[#f2f2f2] pt-16 pb-24 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-[#00d4ff] border-b-2 border-transparent"></div>
            </div>
        );
    }

    if (!post) {
        return (
            <div className="bg-[#0a0a0a] min-h-screen text-[#f2f2f2] pt-16 pb-24 text-center px-6">
                <h1 className="text-3xl font-title uppercase tracking-wider mb-4">Artículo no encontrado</h1>
                <p className="text-gray-400 mb-8">El artículo que buscas no existe o fue movido.</p>
                <Link to="/blog" className="bg-[#00d4ff] text-black px-6 py-3 font-title uppercase tracking-wider">
                    {t.blog_back || 'Volver al Blog'}
                </Link>
            </div>
        );
    }

    const currentUrl = typeof window !== 'undefined' ? encodeURIComponent(window.location.href) : '';
    const shareTitle = encodeURIComponent(post.title);

    return (
        <article className="bg-[#0a0a0a] min-h-screen text-[#f2f2f2] pt-6 md:pt-8 pb-24">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                
                {/* 1. Barra Superior con Migas de Pan y Volver */}
                <div className="flex items-center justify-between mb-8 pb-4 border-b border-white/10 text-xs">
                    <Link 
                        to={getLocalizedPath('/blog', lang)} 
                        className="inline-flex items-center gap-2 text-gray-400 hover:text-[#00d4ff] transition-colors uppercase tracking-widest font-bold"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        <span>{t.blog_back || 'Volver al Blog'}</span>
                    </Link>

                    <div className="hidden sm:flex items-center gap-2 text-gray-500 font-mono text-[11px]">
                        <Compass className="w-3.5 h-3.5 text-[#00d4ff]" />
                        <Link to={getLocalizedPath('/', lang)} className="hover:text-white">{t.nav_home}</Link>
                        <span>/</span>
                        <Link to={getLocalizedPath('/blog', lang)} className="hover:text-white">Blog</Link>
                    </div>
                </div>

                {/* 2. Cabecera del Artículo */}
                <header className="mb-10">
                    <div className="flex flex-wrap items-center gap-3 text-xs text-gray-400 mb-4">
                        {post.categories?.[0] && (
                            <span className="bg-[#00d4ff]/15 text-[#00d4ff] border border-[#00d4ff]/30 px-3 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wider">
                                {post.categories[0].title}
                            </span>
                        )}
                        <div className="flex items-center gap-1.5">
                            <Calendar className="w-3.5 h-3.5 text-[#00d4ff]" />
                            <span>{formatDate(post.publishedAt)}</span>
                        </div>
                        {post.readTimeMinutes && (
                            <div className="flex items-center gap-1.5">
                                <Clock className="w-3.5 h-3.5 text-gray-500" />
                                <span>{post.readTimeMinutes} {t.blog_min_read || 'min de lectura'}</span>
                            </div>
                        )}
                    </div>

                    <h1 className="text-3xl sm:text-4xl md:text-5xl font-title uppercase tracking-tight text-white leading-tight mb-6">
                        {post.title}
                    </h1>

                    {post.excerpt && (
                        <p className="text-gray-300 text-lg sm:text-xl font-light leading-relaxed italic border-l-2 border-[#00d4ff] pl-4 mb-8 text-justify">
                            {post.excerpt}
                        </p>
                    )}

                    {/* Autor */}
                    {post.author && (
                        <div className="flex items-center gap-4 pt-4 border-t border-white/10">
                            {post.author.image?.asset ? (
                                <img 
                                    src={urlFor(post.author.image).width(60).height(60).url()} 
                                    alt={post.author.name}
                                    className="w-12 h-12 rounded-full object-cover border-2 border-[#00d4ff]" 
                                />
                            ) : (
                                <div className="w-12 h-12 rounded-full bg-[#00d4ff]/20 text-[#00d4ff] flex items-center justify-center font-bold text-sm">
                                    <User className="w-6 h-6" />
                                </div>
                            )}
                            <div>
                                <h4 className="text-sm font-bold text-white">{post.author.name}</h4>
                                <p className="text-xs text-gray-400">{post.author.role || 'Guía Local de Montaña'}</p>
                            </div>
                        </div>
                    )}
                </header>

                {/* 3. Imagen de Portada */}
                {post.mainImage?.asset && (
                    <div className="mb-12 rounded-2xl overflow-hidden border border-rock/80 shadow-2xl">
                        <img 
                            src={urlFor(post.mainImage).width(1400).height(800).quality(90).url()} 
                            alt={post.title}
                            className="w-full h-auto max-h-[550px] object-cover" 
                        />
                    </div>
                )}

                {/* 4. Contenido del Artículo */}
                <div className="prose-dark max-w-none mb-16 text-justify">
                    {post.body ? (
                        <PortableText value={post.body} components={portableTextComponents} />
                    ) : (
                        <p className="text-gray-400">Sin contenido disponible.</p>
                    )}
                </div>

                {/* 5. Compartir en Redes Sociales */}
                <div className="p-6 rounded-xl border border-rock bg-[#14100d] flex flex-col sm:flex-row items-center justify-between gap-4 mb-16">
                    <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-gray-300">
                        <Share2 className="w-4 h-4 text-[#00d4ff]" />
                        <span>{t.blog_share || 'Compartir este artículo'}</span>
                    </div>
                    <div className="flex items-center gap-3">
                        {/* WhatsApp */}
                        <a 
                            href={`https://api.whatsapp.com/send?text=${shareTitle}%20${currentUrl}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-3.5 py-1.5 rounded-lg bg-[#25D366]/20 border border-[#25D366]/40 text-[#25D366] hover:bg-[#25D366] hover:text-white text-xs font-bold transition-colors flex items-center gap-1.5"
                        >
                            WhatsApp
                        </a>
                        {/* Facebook */}
                        <a 
                            href={`https://www.facebook.com/sharer/sharer.php?u=${currentUrl}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-3.5 py-1.5 rounded-lg bg-[#1877F2]/20 border border-[#1877F2]/40 text-[#1877F2] hover:bg-[#1877F2] hover:text-white text-xs font-bold transition-colors flex items-center gap-1.5"
                        >
                            Facebook
                        </a>
                        {/* Copiar Enlace */}
                        <button
                            onClick={handleCopyLink}
                            className="px-3.5 py-1.5 rounded-lg bg-white/10 border border-white/20 text-gray-200 hover:bg-white hover:text-black text-xs font-bold transition-colors flex items-center gap-1.5"
                        >
                            {copied ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
                            <span>{copied ? '¡Copiado!' : 'Copiar link'}</span>
                        </button>
                    </div>
                </div>

                {/* 6. Biografía del Autor */}
                {post.author?.bio && (
                    <div className="p-8 rounded-xl border border-rock/80 bg-[#120e0b] mb-16 flex flex-col sm:flex-row items-center sm:items-start gap-6">
                        {post.author.image?.asset && (
                            <img 
                                src={urlFor(post.author.image).width(96).height(96).url()} 
                                alt={post.author.name}
                                className="w-20 h-20 rounded-full object-cover border-2 border-[#00d4ff] shrink-0" 
                            />
                        )}
                        <div className="text-center sm:text-left">
                            <span className="text-[10px] font-bold text-[#00d4ff] uppercase tracking-[0.25em]">Sobre el autor</span>
                            <h4 className="text-xl font-title uppercase text-white mt-1 mb-2">{post.author.name}</h4>
                            <p className="text-sm text-gray-400 font-light leading-relaxed">{post.author.bio}</p>
                        </div>
                    </div>
                )}

                {/* 7. Banner CTA a Expediciones */}
                <div className="p-8 sm:p-12 rounded-2xl border border-[#00d4ff]/40 bg-gradient-to-r from-[#14100d] to-[#0a1924] relative overflow-hidden text-center sm:text-left">
                    <div className="relative z-10 max-w-xl">
                        <span className="text-[10px] font-bold text-[#00d4ff] uppercase tracking-[0.3em] block mb-2">
                            Villa Cerro Castillo, Aysén
                        </span>
                        <h3 className="text-2xl sm:text-3xl font-title uppercase tracking-tight text-white mb-3">
                            {t.blog_cta_title || '¿Inspirado para tu próxima aventura?'}
                        </h3>
                        <p className="text-sm text-gray-300 font-light leading-relaxed mb-6">
                            {t.blog_cta_desc || 'Conoce nuestras expediciones guiadas con seguridad certificada y grupos reducidos en Aysén.'}
                        </p>
                        <Link 
                            to={getLocalizedPath('/expeditions', lang)} 
                            className="inline-flex items-center gap-2 bg-[#00d4ff] hover:bg-white text-black px-6 py-3 font-title text-sm uppercase tracking-widest transition-all duration-300"
                        >
                            <Mountain className="w-4 h-4" />
                            <span>{t.blog_cta_btn || 'Explorar Expediciones'}</span>
                        </Link>
                    </div>
                </div>

            </div>
        </article>
    );
};

export default BlogPost;
